import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Check, Globe, Loader2, Search } from 'lucide-react';
import {
    DEFAULT_LANGUAGE_FLAG,
    GOOGLE_TRANSLATE_LANGUAGE_CODES,
    LANGUAGES,
    LanguageOption,
} from '../../constants/languages';
import { useSettings } from '../../contexts/SettingsContext';
import { useAppDispatch } from '../../hooks/redux';
import { addToast } from '../../store/slices/uiSlice';
import {
    AUTO_GOOGLE_TRANSLATE_SOURCE_LANGUAGE,
    GOOGLE_TRANSLATE_APPLIED_EVENT,
    GOOGLE_TRANSLATE_FAILED_EVENT,
    GOOGLE_TRANSLATE_LANGUAGE_CHANGE_EVENT,
    clearAllGoogleTranslateCookies,
    getCurrentGoogleTranslateTargetLanguage,
    requestGoogleTranslateTargetLanguage,
    resolveGoogleTranslateLanguageCode,
    setGoogleTranslateCookie,
    setStoredGoogleTranslateTargetLanguage,
} from '../../utils/googleTranslate';

type SelectorLanguage = {
    code: string;
    name: string;
    nativeName?: string;
    flag: string;
    aliases: string[];
};

const languageMetadata = new Map<string, LanguageOption>();

LANGUAGES.forEach((language) => {
    languageMetadata.set(language.code, language);
    languageMetadata.set(language.code.toLowerCase(), language);

    if (language.googleCode) {
        languageMetadata.set(language.googleCode, language);
        languageMetadata.set(language.googleCode.toLowerCase(), language);
    }
});

const normalizeLanguageCode = (code?: string | null): string => {
    if (!code) {
        return 'en';
    }

    const trimmedCode = code.trim();
    const match = languageMetadata.get(trimmedCode) || languageMetadata.get(trimmedCode.toLowerCase());
    return resolveGoogleTranslateLanguageCode(match?.googleCode || match?.code || trimmedCode);
};

const normalizeSearchText = (value: string): string =>
    value
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');

const buildLanguageOption = (code: string, fallbackName?: string): SelectorLanguage => {
    const normalizedCode = normalizeLanguageCode(code);
    const metadata = languageMetadata.get(normalizedCode) || languageMetadata.get(normalizedCode.toLowerCase());
    const fallback = fallbackName?.trim();

    return {
        code: normalizedCode,
        name: metadata?.name || fallback || normalizedCode,
        nativeName: metadata?.nativeName,
        flag: metadata?.flag || DEFAULT_LANGUAGE_FLAG,
        aliases: [
            metadata?.code,
            metadata?.googleCode,
            fallback,
            ...(metadata?.aliases || []),
        ].filter((value): value is string => Boolean(value)),
    };
};

const fallbackLanguages: SelectorLanguage[] = GOOGLE_TRANSLATE_LANGUAGE_CODES
    .map((languageCode) => buildLanguageOption(languageCode))
    .filter((language, index, allLanguages) =>
        allLanguages.findIndex((entry) => entry.code === language.code) === index
    )
    .sort((a, b) => a.name.localeCompare(b.name));

const LEGACY_LANGUAGE_CACHE_KEYS = [
    'googleTranslate.languages.v1',
    'googleTranslate.languages.v2',
    'googleTranslate.languages.v3',
];

const LANGUAGE_CACHE_KEY_V3 = 'googleTranslate.languages.v4';

type LanguageSelectorProps = {
    variant?: 'default' | 'appTile';
};

const getCachedLanguages = (): SelectorLanguage[] => {
    try {
        LEGACY_LANGUAGE_CACHE_KEYS.forEach((cacheKey) => {
            window.localStorage.removeItem(cacheKey);
        });

        const rawValue = window.localStorage.getItem(LANGUAGE_CACHE_KEY_V3);
        if (!rawValue) {
            return fallbackLanguages;
        }

        const parsedValue = JSON.parse(rawValue);
        if (!Array.isArray(parsedValue) || parsedValue.length === 0) {
            return fallbackLanguages;
        }

        const cachedLanguages = parsedValue.reduce<SelectorLanguage[]>((accumulator, language) => {
                if (!language || typeof language !== 'object') {
                    return accumulator;
                }

                const code = typeof language.code === 'string' ? language.code : '';
                const name = typeof language.name === 'string' ? language.name : '';
                const nativeName = typeof language.nativeName === 'string' ? language.nativeName : undefined;
                const flag = typeof language.flag === 'string' ? language.flag : DEFAULT_LANGUAGE_FLAG;
                const aliases = Array.isArray(language.aliases)
                    ? language.aliases.filter((value: unknown): value is string => typeof value === 'string')
                    : [];

                if (!code || !name) {
                    return accumulator;
                }

                accumulator.push({
                    code: normalizeLanguageCode(code),
                    name,
                    nativeName,
                    flag,
                    aliases,
                });

                return accumulator;
            }, []);

        return cachedLanguages.length > 0 ? cachedLanguages : fallbackLanguages;
    } catch {
        return fallbackLanguages;
    }
};

const LanguageSelector: React.FC<LanguageSelectorProps> = ({ variant = 'default' }) => {
    const dispatch = useAppDispatch();
    const { settings } = useSettings();
    const appDefaultLanguage = resolveGoogleTranslateLanguageCode(settings?.default_language);
    const sourceLanguage = AUTO_GOOGLE_TRANSLATE_SOURCE_LANGUAGE;
    const [isOpen, setIsOpen] = useState(false);
    const [currentLanguage, setCurrentLanguage] = useState(appDefaultLanguage);
    const [availableLanguages, setAvailableLanguages] = useState<SelectorLanguage[]>(() => getCachedLanguages());
    const [searchQuery, setSearchQuery] = useState('');
    const [isSwitchingLanguage, setIsSwitchingLanguage] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const pendingLanguageRef = useRef<string | null>(null);
    const pendingTimeoutRef = useRef<number | undefined>(undefined);
    const confirmPollRef = useRef<number | undefined>(undefined);
    const confirmedLanguageRef = useRef(appDefaultLanguage);

    const clearPendingTimeout = useCallback(() => {
        if (pendingTimeoutRef.current) {
            window.clearTimeout(pendingTimeoutRef.current);
            pendingTimeoutRef.current = undefined;
        }
    }, []);

    const revertPendingLanguage = useCallback(() => {
        const previousLanguage = confirmedLanguageRef.current || appDefaultLanguage;
        setCurrentLanguage(previousLanguage);
        setStoredGoogleTranslateTargetLanguage(previousLanguage);
        setGoogleTranslateCookie(sourceLanguage, previousLanguage);
    }, [appDefaultLanguage, sourceLanguage]);

    const clearConfirmPoll = useCallback(() => {
        if (confirmPollRef.current) {
            window.clearInterval(confirmPollRef.current);
            confirmPollRef.current = undefined;
        }
    }, []);

    // Reads the REAL on-page translation state to decide whether `lang` is applied.
    // This is the heart of the WebView fix: inside WKWebView the cross-component
    // "applied" event can be missed, so we observe the Google widget + the
    // `translated-ltr/rtl` class that Google sets on <html> directly.
    const isLanguageApplied = useCallback((lang: string): boolean => {
        if (typeof document === 'undefined') {
            return false;
        }

        const select = document.querySelector('.goog-te-combo') as HTMLSelectElement | null;
        if (!select) {
            return false;
        }
        const comboValue = resolveGoogleTranslateLanguageCode(select.value);
        const root = document.documentElement;
        const isTranslated = root.classList.contains('translated-ltr')
            || root.classList.contains('translated-rtl');

        // Switching back to the widget's own page language UN-translates the page.
        // Google notably leaves the `translated-ltr/rtl` class on <html> even after
        // reverting, so the only reliable signal here is the widget returning to the
        // source language. (Verified in a real WKWebView against production.)
        if (lang === appDefaultLanguage) {
            return comboValue === lang;
        }

        // For any other language the page must actually be in a translated state
        // AND the widget must be pointed at the requested language.
        return comboValue === lang && isTranslated;
    }, [appDefaultLanguage]);

    const finalizeLanguageSwitch = useCallback((lang: string, success: boolean) => {
        if (pendingLanguageRef.current !== lang) {
            return;
        }

        pendingLanguageRef.current = null;
        clearPendingTimeout();
        clearConfirmPoll();
        setIsSwitchingLanguage(false);

        const language = availableLanguages.find((entry) => entry.code === lang)
            || buildLanguageOption(lang, lang.toUpperCase());

        if (success) {
            confirmedLanguageRef.current = lang;
            setCurrentLanguage(lang);
            dispatch(addToast({
                type: 'success',
                message: `${language.nativeName || language.name} applied`,
            }));
        } else {
            revertPendingLanguage();
            dispatch(addToast({
                type: 'error',
                message: `Could not switch to ${language.nativeName || language.name}`,
            }));
        }
    }, [availableLanguages, clearConfirmPoll, clearPendingTimeout, dispatch, revertPendingLanguage]);

    useEffect(() => {
        const syncCurrentLanguage = () => {
            if (pendingLanguageRef.current) {
                return;
            }

            const nextLanguage = normalizeLanguageCode(getCurrentGoogleTranslateTargetLanguage(appDefaultLanguage));
            confirmedLanguageRef.current = nextLanguage;
            setCurrentLanguage(nextLanguage);
        };

        syncCurrentLanguage();

        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        window.addEventListener('google-translate-ready', syncCurrentLanguage);
        window.addEventListener(GOOGLE_TRANSLATE_LANGUAGE_CHANGE_EVENT, syncCurrentLanguage);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('google-translate-ready', syncCurrentLanguage);
            window.removeEventListener(GOOGLE_TRANSLATE_LANGUAGE_CHANGE_EVENT, syncCurrentLanguage);
        };
    }, [appDefaultLanguage]);

    useEffect(() => {
        const getEventLanguage = (event: Event) => {
            const detail = (event as CustomEvent<{ targetLanguage?: string }>).detail;
            return normalizeLanguageCode(detail?.targetLanguage || pendingLanguageRef.current || currentLanguage);
        };

        // The cross-component events are now only a fast-path hint. The confirmation
        // poll in handleLanguageChange (which reads the real page state) is the source
        // of truth, so a missed/duplicated event inside the WebView can no longer
        // strand the UI on "Switching...".
        const handleApplied = (event: Event) => {
            const appliedLanguage = getEventLanguage(event);

            if (pendingLanguageRef.current) {
                if (appliedLanguage === pendingLanguageRef.current) {
                    finalizeLanguageSwitch(appliedLanguage, true);
                }
                return;
            }

            // No user-initiated switch in flight — just keep the indicator in sync.
            confirmedLanguageRef.current = appliedLanguage;
            setCurrentLanguage(appliedLanguage);
        };

        const handleFailed = (event: Event) => {
            const failedLanguage = getEventLanguage(event);

            // Only trust a "failed" signal if the page genuinely is not on the
            // target language; otherwise the confirmation poll will catch the
            // success shortly. This prevents false "Could not switch" toasts.
            if (pendingLanguageRef.current === failedLanguage && !isLanguageApplied(failedLanguage)) {
                finalizeLanguageSwitch(failedLanguage, false);
            }
        };

        window.addEventListener(GOOGLE_TRANSLATE_APPLIED_EVENT, handleApplied);
        window.addEventListener(GOOGLE_TRANSLATE_FAILED_EVENT, handleFailed);

        return () => {
            window.removeEventListener(GOOGLE_TRANSLATE_APPLIED_EVENT, handleApplied);
            window.removeEventListener(GOOGLE_TRANSLATE_FAILED_EVENT, handleFailed);
        };
    }, [currentLanguage, finalizeLanguageSwitch, isLanguageApplied]);

    useEffect(() => () => {
        clearPendingTimeout();
        clearConfirmPoll();
    }, [clearPendingTimeout, clearConfirmPoll]);

    useEffect(() => {
        let isActive = true;
        let timeoutId: number | undefined;

        const syncLanguagesFromGoogleWidget = (): boolean => {
            const select = document.querySelector('.goog-te-combo') as HTMLSelectElement | null;

            if (!select || select.options.length === 0) {
                return false;
            }

            const nextLanguages = Array.from(select.options)
                .map((option) => option.value.trim() ? buildLanguageOption(option.value, option.text) : null)
                .filter((option): option is SelectorLanguage => Boolean(option))
                .filter((option, index, allOptions) => allOptions.findIndex((entry) => entry.code === option.code) === index)
                .sort((a, b) => a.name.localeCompare(b.name));

            if (!nextLanguages.some((language) => language.code === appDefaultLanguage)) {
                nextLanguages.unshift(buildLanguageOption(appDefaultLanguage));
            }

            if (isActive && nextLanguages.length > 0) {
                setAvailableLanguages(nextLanguages);
                window.localStorage.setItem(LANGUAGE_CACHE_KEY_V3, JSON.stringify(nextLanguages));
            }

            return nextLanguages.length > 0;
        };

        const pollForGoogleWidget = () => {
            if (syncLanguagesFromGoogleWidget()) {
                return;
            }

            timeoutId = window.setTimeout(pollForGoogleWidget, 500);
        };

        pollForGoogleWidget();

        return () => {
            isActive = false;

            if (timeoutId) {
                window.clearTimeout(timeoutId);
            }
        };
    }, [appDefaultLanguage]);

    const handleLanguageChange = (langCode: string) => {
        const nextLanguage = normalizeLanguageCode(langCode);
        const language = availableLanguages.find((entry) => entry.code === nextLanguage)
            || buildLanguageOption(nextLanguage, nextLanguage.toUpperCase());

        setIsOpen(false);

        // Already on this language and the page reflects it — no-op. Re-dispatching
        // would make the Google widget reset to source and flash back.
        if (nextLanguage === confirmedLanguageRef.current && isLanguageApplied(nextLanguage)) {
            setCurrentLanguage(nextLanguage);
            return;
        }

        pendingLanguageRef.current = nextLanguage;
        setIsSwitchingLanguage(true);

        dispatch(addToast({
            type: 'info',
            message: `Switching to ${language.nativeName || language.name}...`,
        }));

        // Trigger the actual translation.
        clearAllGoogleTranslateCookies();
        requestGoogleTranslateTargetLanguage(nextLanguage, sourceLanguage);

        // Self-contained confirmation: watch the real on-page translation state
        // rather than waiting on a cross-component event that WKWebView can drop.
        const maxWaitMs = 15000;
        const startedAt = Date.now();

        clearConfirmPoll();
        confirmPollRef.current = window.setInterval(() => {
            if (pendingLanguageRef.current !== nextLanguage) {
                clearConfirmPoll();
                return;
            }

            if (isLanguageApplied(nextLanguage)) {
                finalizeLanguageSwitch(nextLanguage, true);
            } else if (Date.now() - startedAt >= maxWaitMs) {
                finalizeLanguageSwitch(nextLanguage, false);
            }
        }, 300);

        // Hard safety net — re-check the real state once more before giving up.
        clearPendingTimeout();
        pendingTimeoutRef.current = window.setTimeout(() => {
            if (pendingLanguageRef.current !== nextLanguage) {
                return;
            }
            finalizeLanguageSwitch(nextLanguage, isLanguageApplied(nextLanguage));
        }, maxWaitMs + 1500);
    };

    const normalizedSearchQuery = normalizeSearchText(searchQuery);
    const filteredLanguages = availableLanguages.filter((language) => {
        if (!normalizedSearchQuery) {
            return true;
        }

        const searchableText = normalizeSearchText([
            language.name,
            language.nativeName,
            language.code,
            ...language.aliases,
        ].filter((value): value is string => Boolean(value)).join(' '));

        return searchableText.includes(normalizedSearchQuery);
    });

    const currentLangObj = availableLanguages.find((language) => language.code === currentLanguage)
        || buildLanguageOption(currentLanguage, currentLanguage.toUpperCase());
    const isAppTile = variant === 'appTile';
    const dropdownClassName = isAppTile
        ? 'fixed bg-white dark:bg-[#131a45] rounded-xl sm:rounded-2xl shadow-2xl border border-gray-200 dark:border-[#1e2a5e] z-[300] flex flex-col overflow-hidden'
        : 'fixed sm:absolute right-2 sm:right-0 top-[3.75rem] sm:top-auto sm:mt-2 w-[calc(100vw-1rem)] sm:w-72 max-w-xs sm:max-w-none bg-white dark:bg-[#131a45] rounded-xl sm:rounded-2xl shadow-2xl border border-gray-200 dark:border-[#1e2a5e] z-[200] flex flex-col max-h-[calc(100vh-5rem)] sm:max-h-none overflow-hidden';
    const dropdownStyle = isAppTile
        ? {
            left: '1rem',
            right: '1rem',
            bottom: 'calc(env(safe-area-inset-bottom) + 7rem)',
            maxHeight: '52vh',
        }
        : undefined;

    return (
        <div className="relative" ref={dropdownRef}>

            <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setIsOpen(!isOpen)}
                className={isAppTile
                    ? 'flex min-h-[64px] w-full flex-col items-center justify-center gap-1.5 rounded-2xl border border-gray-100 bg-white px-2 py-3 text-center text-gray-600 shadow-sm transition-all hover:border-emerald-300 hover:text-emerald-600 dark:border-[#34447e] dark:bg-[#131a45] dark:text-[#d8e6f8] dark:hover:border-emerald-300/60 dark:hover:text-emerald-300'
                    : 'flex items-center gap-1 sm:gap-1.5 px-2 sm:px-2.5 lg:px-3 py-1.5 sm:py-2 rounded-lg sm:rounded-xl bg-gray-100 hover:bg-gray-200 dark:bg-[#131a45] dark:hover:bg-[#1e2a5e] border border-gray-200 dark:border-[#1e2a5e] transition-all group'}
                aria-label="Change language"
            >
                {isAppTile ? (
                    <>
                        {isSwitchingLanguage ? (
                            <Loader2 className="h-5 w-5 animate-spin text-emerald-500" />
                        ) : (
                            <Globe className="h-5 w-5 text-emerald-500" />
                        )}
                        <span className="text-[10px] font-bold leading-tight">
                            {isSwitchingLanguage ? 'Switching' : 'Language'}
                        </span>
                    </>
                ) : (
                    <>
                        {isSwitchingLanguage ? (
                            <Loader2 className="w-4 h-4 flex-shrink-0 animate-spin text-emerald-500" />
                        ) : (
                            <Globe className="w-4 h-4 flex-shrink-0 text-emerald-500 group-hover:text-emerald-400 transition-colors" />
                        )}
                        <span className="hidden sm:inline text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap">
                            {currentLangObj.flag} <span className="ml-1">{currentLangObj.nativeName || currentLangObj.name}</span>
                        </span>
                        <ChevronDown className={`w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0 text-gray-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
                    </>
                )}
            </motion.button>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                        className={dropdownClassName}
                        style={dropdownStyle}
                    >
                        <div className="p-3 border-b border-gray-100 dark:border-[#1e2a5e]">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search language..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full pl-9 pr-4 py-2 bg-gray-50 dark:bg-[#0a0e27] border border-gray-200 dark:border-[#1e2a5e] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-all"
                                    autoFocus
                                />
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto p-2" style={{ maxHeight: '20rem', WebkitOverflowScrolling: 'touch', touchAction: 'pan-y' }}>
                            <div className="grid grid-cols-1 gap-1">
                                {filteredLanguages.length > 0 ? (
                                    filteredLanguages.map((lang) => (
                                        <button
                                            key={lang.code}
                                            onClick={() => handleLanguageChange(lang.code)}
                                            disabled={isSwitchingLanguage && pendingLanguageRef.current === lang.code}
                                            className={`flex items-center justify-between px-3 py-2.5 rounded-lg sm:rounded-xl text-xs sm:text-sm transition-all ${currentLanguage === lang.code
                                                ? 'bg-emerald-500/10 text-emerald-500 font-bold'
                                                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#1e2a5e]'
                                                } disabled:cursor-wait disabled:opacity-70`}
                                        >
                                            <div className="flex items-center gap-3 min-w-0 text-left">
                                                <span className="text-lg sm:text-xl">{lang.flag}</span>
                                                <div className="min-w-0">
                                                    <div className="truncate">{lang.name}</div>
                                                    {lang.nativeName && lang.nativeName !== lang.name && (
                                                        <div className="text-[11px] opacity-70 truncate">{lang.nativeName}</div>
                                                    )}
                                                </div>
                                            </div>
                                            {currentLanguage === lang.code && <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-500" />}
                                        </button>
                                    ))
                                ) : (
                                    <div className="py-8 text-center text-gray-400 text-sm">
                                        No languages found
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="px-4 py-3 bg-gray-50 dark:bg-[#0a0e27] border-top border-gray-100 dark:border-[#1e2a5e] text-[10px] text-gray-400 text-center">
                            {availableLanguages.length} languages available
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

        </div>
    );
};

export default LanguageSelector;
